import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useService } from '~/services/service.base';
import { ContactsService } from '~/services/service.contacts';
import { UIService } from '~/services/service.ui';
import { Row } from '~/views/atoms/boxes';
import { Breadcrumb } from '~/views/molecules/nav.breadcrumbs';
import { PageHeader } from '~/views/molecules/page.header';
import { TabsFilter } from '~/views/molecules/tabs.filter';
import { PageWrapper } from '~/views/organisms/wrapper.page';
import { ServiceCommunityUser } from '~/services/service.community.user';
import { ServiceCommunity } from '~/services/service.community';
import { SimpleTable } from '~/views/molecules/simple-table';
import { EBreadcrumbKey, EContactsFilter, ETextType } from '~/shared/types';
import { ServiceContactInfo } from '~/services/service.contact.info';
import { ContactInfo } from '~/views/organisms/contact-info/contact.info';
import { reaction } from 'mobx';
import { useStyles } from '~/pages/funds/contacts/styles';
export const PageFundsContacts = observer(() => {
    const { t } = useTranslation();
    const { breadcrumbsSvc } = useService(UIService);
    const { filterSvc } = useService(ContactsService);
    const communityUsersService = useService(ServiceCommunityUser);
    const communityService = useService(ServiceCommunity);
    const contactInfoService = useService(ServiceContactInfo);
    const classes = useStyles();
    const closeButtonHandler = useCallback(() => {
        contactInfoService.openContactInfoService.handleClose();
    }, [contactInfoService.openContactInfoService]);
    const config = useMemo(() => {
        return {
            headerColumns: [
                {
                    label: t('CONTACTS_PAGE_TABLE_NAME'),
                    columnWidth: 5,
                },
                {
                    label: t('CONTACTS_PAGE_TABLE_CONTACTS'),
                    columnWidth: 5,
                },
                {
                    columnWidth: 2,
                },
            ],
            rowColumns: [
                {
                    fieldKey: 'fullName',
                    columnWidth: 5,
                    variant: 'body2',
                    alignItems: 'center',
                },
                {
                    fieldKey: ['email', 'contactNumber'],
                    delimiter: '/',
                    alignItems: 'center',
                    columnWidth: 5,
                    text: 'secondary',
                    className: classes.elipsis,
                    variant: 'body2',
                    textType: [ETextType.email, ETextType.tel],
                },
                {
                    fieldKey: 'eyeIcon',
                    columnWidth: 2,
                    callback: (column, item) => {
                        communityUsersService.setSelectedUser(item);
                        contactInfoService.openContactInfoService.handleOpen();
                    },
                },
            ],
        };
    }, [classes.elipsis, communityUsersService, contactInfoService.openContactInfoService, t]);
    useEffect(() => {
        if (!communityService.currentCommunity?.id) {
            return;
        }
        communityUsersService.getUsers(communityService.currentCommunity.id);
    }, [communityService.currentCommunity.id, communityUsersService]);
    useEffect(() => {
        return reaction(() => filterSvc.tab, tab => {
            communityUsersService.filterUsersByMemberType(tab);
        });
    }, [communityUsersService, filterSvc.tab]);
    useEffect(() => {
        breadcrumbsSvc.use([
            {
                key: EBreadcrumbKey.repository,
            },
        ]);
    }, [breadcrumbsSvc]);
    filterSvc.use([
        { value: EContactsFilter.all, label: t('CONTACTS_TABFILTER_ALL') },
        { value: EContactsFilter.contributor, label: t('CONTACTS_TABFILTER_CONTRIBUTORS') },
        { value: EContactsFilter.member, label: t('CONTACTS_TABFILTER_MEMBERS') },
    ]);
    return (_jsxs(PageWrapper, { children: [_jsx(Breadcrumb.Nav, { children: breadcrumbsSvc.children }), _jsx(PageHeader, { children: _jsx(Typography, { variant: "h2", children: t('BOTTOM_NAV_CONTACTS') }) }), _jsx(Row, { px: 2, py: 1.5, children: _jsx(TabsFilter, { items: filterSvc.tabs, value: filterSvc.tab, onChange: filterSvc.handleChange }) }), _jsx(Row, { children: _jsx(SimpleTable, { data: communityUsersService.users.toJSON(), config: config }) }), contactInfoService.openContactInfoService.open && (_jsx(ContactInfo, { open: contactInfoService.openContactInfoService.open, item: communityUsersService.selectedUser, closeButtonHandler: closeButtonHandler }))] }));
});
